import React, {useState} from 'react';
import {Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, LinearProgress, TextField, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {campaignFormActions as actions} from '../../store/actions/campaignFormAction';
import LandingAddPopUp from '../landings/LandingAddPopUp';
import LandingEditPopUp from '../landings/LandingEditPopUp';
import OfferAddPopUp from '../offers/OfferAddPopUp';
import OfferEditPopUp from '../offers/OfferEditPopUp';
import CountrySelect from '../utils/CountrySelect';
import WorkspaceDropdown from '../workspace/WorkspaceDropdown';
import ReactHookTagsSelect from '../tags/ReactHookTagsSelect';
import {FlowSection} from './FlowDesign';
import {getSettingsColors} from '../settings/SettingsDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import FlowPaths from './FlowPaths';
import FlowDestinations from './FlowDestinations';
import FlowConditions from './FlowConditions';

export function getFlowWeight(weight, items) {
    const total = items.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
    return total > 0 ? Math.round((Number(weight) || 0) / total * 100) : 0;
}

export default function FlowForm({title, control, register, handleClose, errors, handleSubmit, onSubmit, loading,
    flowData, getValues, setValue, requestError, watch, saveDisabled = false, submitLabel = 'Save changes'}) {
    const formId = React.useId();
    const dispatch = useDispatch();
    const [modal, setModal] = useState(null);
    const closeModal = name => {
        if (modal?.mode === 'edit' && typeof name === 'string' && name) {
            const update = modal.type === 'landing' ? actions.updateLandingNameInPath : actions.updateOfferNameInPath;
            dispatch(update(modal.ruleIndex, modal.pathIndex, modal.index, modal.id, name));
        }
        setModal(null);
    };
    const {flow, selectedRuleNumber, selectedPathNumber, selectedRule} = useSelector(state => state.campaignForm);
    const rule = flow.rules[selectedRuleNumber];
    const path = rule?.paths[selectedPathNumber];
    return <>
        <DialogTitle id="flow-form-title">{title}
            <SettingsDialogDescription>Build reusable traffic routing with weighted paths, landings, offers and targeting rules.</SettingsDialogDescription>
        </DialogTitle>
        {loading && <LinearProgress aria-label="Loading flow" sx={{mx: {xs: 2, sm: 3}, borderRadius: 1}}/>}
        <DialogContent>
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            <Box component="form" id={formId} onSubmit={handleSubmit(data => onSubmit({data, flow}))} autoComplete="off">
                <Box component="fieldset" disabled={loading} sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                    <FlowSection title="Flow details">
                        <Box className="flow-details-inputs" sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                            <WorkspaceDropdown control={control} workspaceId={flowData?.workspaceId} setValue={setValue}
                                validate={value => value?.value > 0 || 'Please select a workspace.'}/>
                            <TextField {...register('name', {required: 'Enter a flow name.', maxLength: {value: 255, message: 'Use at most 255 characters.'}})}
                                error={!!errors.name} helperText={errors.name?.message} label="Name *" placeholder="Flow name" fullWidth size="small" slotProps={{inputLabel: {shrink: true}}}/>
                            <CountrySelect control={control} compact/>
                            <Box sx={{minWidth: 0, '& .MuiAutocomplete-root > .MuiBox-root': {alignItems: 'center'},
                                '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:first-of-type': {minWidth: 0},
                                '& .MuiAutocomplete-root > .MuiBox-root > .MuiBox-root:last-child': {py: 0, '& > div': {paddingTop: '0 !important'}}}}>
                                <ReactHookTagsSelect control={control} inputName="tags" inputLabel="Tags" inputLabelShrink getValues={getValues}/>
                            </Box>
                        </Box>
                        <Typography sx={{fontSize: 12, color: 'text.secondary'}}>Country helps organize your flows. It does not affect traffic distribution.</Typography>
                    </FlowSection>
                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.9fr) minmax(0, 1.1fr)'}, gap: 2, alignItems: 'start'}}>
                        <FlowPaths getCurrentWeight={getFlowWeight}/>
                        {selectedRule !== null ? <FlowSection title="Rule conditions" description="Visitors matching these conditions use this rule’s paths."
                            sx={{bgcolor: theme => getSettingsColors(theme).card}}>
                            <FlowConditions key={selectedRuleNumber}/>
                        </FlowSection> : <FlowSection title={path?.name || 'Path destinations'} description={`Configure destinations for ${rule?.name || 'Default'}. Select another path to edit its destinations.`}>
                            <FlowDestinations key={`${selectedRuleNumber}-${selectedPathNumber}`} onOpenModal={setModal} control={control} errors={errors} setValue={setValue} countryCode={watch('country')?.code} getCurrentWeight={getFlowWeight}/>
                        </FlowSection>}
                    </Box>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button type="button" color="inherit" disabled={loading} onClick={handleClose}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained" disabled={loading || saveDisabled} startIcon={loading ? <CircularProgress size={16} color="inherit"/> : undefined}>
                {loading ? 'Please wait…' : submitLabel}
            </Button>
        </DialogActions>
        {modal?.type === 'landing' && modal.mode === 'add' && <LandingAddPopUp open onClose={closeModal}/>}
        {modal?.type === 'landing' && modal.mode === 'edit' && <LandingEditPopUp open onClose={closeModal} landingId={modal.id}/>}
        {modal?.type === 'offer' && modal.mode === 'add' && <OfferAddPopUp open onClose={closeModal}/>}
        {modal?.type === 'offer' && modal.mode === 'edit' && <OfferEditPopUp open onClose={closeModal} offerId={modal.id}/>}
    </>;
}
